/** @format */
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject, Injectable } from '@angular/core';
import {
	attributeSelected,
	selectAttributeOptionsUpdated,
	skusFilteredForResolution,
	stateInitAttributeSelected,
	trueFitRecommendedSizeSelected
} from './attributes.actions';
import { filter, map } from 'rxjs/operators';
import { AttributeOption } from '../interfaces/attributes-v2.interface';
import { ProductSku } from '@shared/data-access/interfaces';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { selectSelectedAttributeOptions } from './attributes.selectors';
import {
	selectDefiningAttributesLength,
	skuResolutionChangedViaAttributes
} from '@pdp/data-access/state';
import { SkuResolutionService } from '../../../../data-access-new/services/src/lib/product-selection/sku-resolution.service';

@Injectable()
export class AttributesEffects {
	private readonly actions$ = inject(Actions);
	private readonly store = inject(Store);

	// Build a list of SKUs that match the newly selected attribute so we can resolve a SKU and update selected attribute options
	matchSkusWithSelectedAttributeForPossibleResolve$ = createEffect(() =>
		this.actions$.pipe(
			ofType(attributeSelected, stateInitAttributeSelected, trueFitRecommendedSizeSelected),
			map(({ allProductSkus, selectedAttribute, alternativeInStockRecsEnabled }) => {
				const matchedSkus = allProductSkus.filter((sku) => {
					let validAttributes = [];
					if (sku.definingAttributes) {
						// checking each defining attribute against selections for matches
						validAttributes = sku.definingAttributes.filter((attr) => {
							if (
								attr.value.toLowerCase() ===
									selectedAttribute.value.toLowerCase() &&
								attr.name.toLowerCase() === selectedAttribute.name.toLowerCase()
							) {
								return true;
							}
							return false;
						});
						return validAttributes.length > 0;
					}
					return false;
				});
				return skusFilteredForResolution({
					matchedSkus,
					selectedAttribute,
					alternativeInStockRecsEnabled
				});
			})
		)
	);

	updateSelectedAttributeOptions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(skusFilteredForResolution),
			concatLatestFrom(() => this.store.select(selectSelectedAttributeOptions)),
			map(
				([
					{ matchedSkus, selectedAttribute, alternativeInStockRecsEnabled },
					currentAttributeOptions
				]) => {
					const availableAttributeOptions =
						this.getAvailableAttributeOptionsForSelectedAttribute(
							selectedAttribute,
							matchedSkus
						);

					const selectedAttrWithAvailableAttrs: AttributeOption = {
						...selectedAttribute,
						availableAttributeOptions
					};

					const updatedSelectedAttributeOptions =
						this.filterCurrentSelectedAttributeOptionsForNewSelection(
							currentAttributeOptions,
							selectedAttrWithAvailableAttrs,
							alternativeInStockRecsEnabled
						);

					return selectAttributeOptionsUpdated({
						allSelectedAttributeOptions: updatedSelectedAttributeOptions,
						matchedSkus // Pass through matched SKUs for the SKU resolution effect
					});
				}
			)
		)
	);

	checkForResolvedSku$ = createEffect(() =>
		this.actions$.pipe(
			ofType(selectAttributeOptionsUpdated),
			concatLatestFrom(() => this.store.select(selectDefiningAttributesLength)),
			filter(
				([{ allSelectedAttributeOptions }, skuDefiningAttributesLength]) =>
					allSelectedAttributeOptions.length === skuDefiningAttributesLength
			),
			map(([{ allSelectedAttributeOptions, matchedSkus }]) => {
				const attributeMap: Record<string, string> = {};
				allSelectedAttributeOptions.forEach((option) => {
					attributeMap[option.name] = option.value;
				});

				const resolvedSku = SkuResolutionService.getResolvedSku(matchedSkus, attributeMap);

				return skuResolutionChangedViaAttributes({ resolvedSku });
			})
		)
	);

	// Build the available attribute options for the selected attribute based on the available SKUs
	private getAvailableAttributeOptionsForSelectedAttribute(
		selectedAttribute: AttributeOption,
		availableSkus: ProductSku[]
	) {
		const availableAttributeOptions: string[] = [];

		for (const availableSku of availableSkus) {
			for (const attribute of availableSku.definingAttributes) {
				const attributeIdentifier = `${attribute.name}${attribute.value}`;

				const isDifferentAttribute =
					attribute.name.toLowerCase() !== selectedAttribute.name.toLowerCase();
				const isNotAlreadyIncluded =
					!availableAttributeOptions.includes(attributeIdentifier);

				if (isDifferentAttribute && isNotAlreadyIncluded) {
					availableAttributeOptions.push(attributeIdentifier);
				}
			}
		}

		return availableAttributeOptions;
	}

	// Remove any currently selected attributes that aren't available with the new selected attribute
	private filterCurrentSelectedAttributeOptionsForNewSelection(
		currentAttributeOptions: AttributeOption[],
		selectedAttrWithAvailableAttrs: AttributeOption,
		alternativeInStockRecsEnabled: boolean
	) {
		return currentAttributeOptions
			.filter((attr) => {
				// Remove non-existent attributes and the newly selected attribute
				if (
					!attr ||
					selectedAttrWithAvailableAttrs.name.toLowerCase() === attr.name.toLowerCase()
				) {
					return false;
				}

				if (alternativeInStockRecsEnabled) {
					return true;
				}

				return selectedAttrWithAvailableAttrs.availableAttributeOptions.some(
					(availableAttribute: string) =>
						availableAttribute.toLowerCase() ===
						`${attr.name}${attr.value}`.toLowerCase()
				);
			})
			.concat(selectedAttrWithAvailableAttrs);
	}
}
